/**
 * Máy chủ giả của hồ sơ nhận diện tổ chức (ADR-UI-0016).
 *
 * Đóng vai tenant-service theo `openapi/platform-api.yaml` (nhóm `tenant-profile`) khi chưa có
 * backend hoặc khi URL có `?mock=`. Từ chối **y như hợp đồng**:
 *
 * - `If-Match` lệch phiên bản hiện hành: `412 VERSION_MISMATCH`.
 * - Ảnh sai định dạng: `415`; quá 2 MB: `413`; cả hai mang mã `INVALID_ARGUMENT`.
 * - Khôi phục phiên bản không có: `404`; trùng nội dung hiện hành thì không tạo phiên bản và trả hiện hành.
 * - Sửa mà không đổi gì thì **không** tạo phiên bản mới.
 *
 * Để diễn `412`: mở màn ở hai tab, lưu ở tab này rồi lưu ở tab kia. Trạng thái
 * nằm trong bộ nhớ, mất khi tải lại trang.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import { kiemTepLogo } from '../modules/ho-so-to-chuc/nghiep-vu/ho-so';
import type {
  LogoSlot,
  TenantProfile,
  TenantProfileChange,
  TenantProfileUpdate,
  TenantProfileVersion,
  TenantProfileVersionPage,
  TruongDoi,
} from '../modules/ho-so-to-chuc/types';

const loi = (code: string, status: number, message: string): ApiError => ({ code, status, message });

/** Lỗi ảnh của backend thật là `INVALID_ARGUMENT` kèm 413/415/422, không có mã riêng; `message` là câu tiếng Việt của server. */
const loiAnh = (status: number, message: string): ApiError => loi('INVALID_ARGUMENT', status, message);

const NGAY = 24 * 60 * 60 * 1000;
const luc = (soNgay: number) => new Date(Date.now() + soNgay * NGAY).toISOString();

/** Ảnh và hash theo từng ô; hash là thứ lịch sử ghi, không phải tệp. */
interface Anh {
  url: string | null;
  hash: string | null;
}

interface BanChup {
  display_name: string;
  short_name: string;
  anh: Record<LogoSlot, Anh>;
}

const TRUONG_ANH: Record<LogoSlot, TruongDoi> = {
  light: 'logo_light',
  dark: 'logo_dark',
  favicon: 'favicon',
};

const TRONG: Anh = { url: null, hash: null };

const chup = (b: BanChup): BanChup => ({
  display_name: b.display_name,
  short_name: b.short_name,
  anh: { light: { ...b.anh.light }, dark: { ...b.anh.dark }, favicon: { ...b.anh.favicon } },
});

/** Diff hai bản chụp theo thứ tự hiển thị cố định. */
function diff(truoc: BanChup, sau: BanChup): TenantProfileChange[] {
  const ra: TenantProfileChange[] = [];
  if (truoc.display_name !== sau.display_name) {
    ra.push({ field: 'display_name', before: truoc.display_name, after: sau.display_name });
  }
  if (truoc.short_name !== sau.short_name) {
    ra.push({ field: 'short_name', before: truoc.short_name, after: sau.short_name });
  }
  for (const slot of ['light', 'dark', 'favicon'] as const) {
    if (truoc.anh[slot].hash !== sau.anh[slot].hash) {
      ra.push({ field: TRUONG_ANH[slot], before: truoc.anh[slot].hash, after: sau.anh[slot].hash });
    }
  }
  return ra;
}

const BAN_DAU: BanChup[] = [
  {
    display_name: 'Minh Long',
    short_name: 'ML',
    anh: { light: TRONG, dark: TRONG, favicon: TRONG },
  },
  {
    display_name: 'Công ty Cổ phần Minh Long',
    short_name: 'ML',
    anh: { light: TRONG, dark: TRONG, favicon: TRONG },
  },
  {
    display_name: 'Công ty Cổ phần Minh Long',
    short_name: 'Minh Long',
    anh: { light: TRONG, dark: TRONG, favicon: TRONG },
  },
];

interface Muc {
  ban: TenantProfileVersion;
  chup: BanChup;
}

function dungLichSu(): Muc[] {
  const nguoi = ['Phạm Thu Hà', 'Phạm Thu Hà', 'Nguyễn Văn An'];
  const ngay = [-40, -12, -3];
  return BAN_DAU.map((b, i) => ({
    ban: {
      version: i + 1,
      created_at: luc(ngay[i]),
      created_by: nguoi[i],
      changes: diff(
        i === 0
          ? { display_name: '', short_name: '', anh: { light: TRONG, dark: TRONG, favicon: TRONG } }
          : BAN_DAU[i - 1],
        b,
      ),
      restored_from: null,
    },
    chup: chup(b),
  }));
}

let lichSu: Muc[] = dungLichSu();

function hienHanh(): Muc {
  return lichSu[lichSu.length - 1];
}

function tuMuc(m: Muc): TenantProfile {
  return {
    display_name: m.chup.display_name,
    short_name: m.chup.short_name,
    logo_light_url: m.chup.anh.light.url,
    logo_dark_url: m.chup.anh.dark.url,
    favicon_url: m.chup.anh.favicon.url,
    version: m.ban.version,
    updated_at: m.ban.created_at,
    updated_by: m.ban.created_by,
  };
}

const NGUOI_SUA = 'Phạm Thu Hà';

/** Ghi một phiên bản mới nếu có thay đổi thật; không thì trả hiện hành. */
function ghi(moi: BanChup, restoredFrom: number | null = null): TenantProfile {
  const cu = hienHanh();
  const doi = diff(cu.chup, moi);
  if (doi.length === 0) return tuMuc(cu);
  lichSu.push({
    ban: {
      version: cu.ban.version + 1,
      created_at: new Date().toISOString(),
      created_by: NGUOI_SUA,
      changes: doi,
      restored_from: restoredFrom,
    },
    chup: chup(moi),
  });
  return tuMuc(hienHanh());
}

function kiemPhienBan(ifMatch: number): void {
  if (ifMatch !== hienHanh().ban.version) {
    throw loi('VERSION_MISMATCH', 412, 'Hồ sơ đã được thay đổi ở nơi khác.');
  }
}

/** Băm giả đủ ổn định để hai tệp giống nhau ra cùng hash. */
function bamGia(tep: File): string {
  let h = 5381;
  const s = `${tep.name}:${tep.size}:${tep.type}`;
  for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(16).padStart(8, '0');
}

export const mayChuHoSoToChuc = {
  doc: (): TenantProfile => tuMuc(hienHanh()),

  /** Tổ chức mới, chưa cấu hình gì — `?mock=empty`. */
  macDinhRong: (): TenantProfile => ({
    display_name: 'Tổ chức mới',
    short_name: 'Mới',
    logo_light_url: null,
    logo_dark_url: null,
    favicon_url: null,
    version: 1,
    updated_at: new Date().toISOString(),
    updated_by: null,
  }),

  capNhat: (du: TenantProfileUpdate, ifMatch: number): TenantProfile => {
    kiemPhienBan(ifMatch);
    const cu = hienHanh().chup;
    const moi = chup(cu);
    if (du.display_name !== undefined) moi.display_name = du.display_name.trim();
    if (du.short_name !== undefined) moi.short_name = du.short_name.trim();
    if (!moi.display_name || !moi.short_name) {
      throw {
        ...loi('INVALID_ARGUMENT', 400, ''),
        details: [
          { pointer: !moi.display_name ? '#/display_name' : '#/short_name', detail: 'Không được để trống.' },
        ],
      } satisfies ApiError;
    }
    return ghi(moi);
  },

  thayLogo: (slot: LogoSlot, tep: File, ifMatch: number): TenantProfile => {
    kiemPhienBan(ifMatch);
    const sai = kiemTepLogo(tep);
    if (sai === 'hoSoToChuc.loi.tepDinhDang') {
      throw loiAnh(415, 'Chỉ nhận ảnh PNG hoặc JPEG. SVG và các định dạng khác không được hỗ trợ, hãy xuất ảnh sang PNG.');
    }
    if (sai === 'hoSoToChuc.loi.tepQuaLon') {
      throw loiAnh(413, 'Ảnh lớn hơn 2 MB. Chọn ảnh nhỏ hơn hoặc nén lại rồi tải lên.');
    }
    const moi = chup(hienHanh().chup);
    // Ảnh nằm trong bộ nhớ trình duyệt; backend thật trả đường dẫn Gateway.
    moi.anh[slot] = { url: URL.createObjectURL(tep), hash: bamGia(tep) };
    return ghi(moi);
  },

  goLogo: (slot: LogoSlot, ifMatch: number): TenantProfile => {
    kiemPhienBan(ifMatch);
    const moi = chup(hienHanh().chup);
    moi.anh[slot] = { ...TRONG };
    return ghi(moi);
  },

  dsPhienBan: (): TenantProfileVersionPage => ({
    items: lichSu.map((m) => ({ ...m.ban, changes: [...m.ban.changes] })).reverse(),
    next_cursor: null,
  }),

  khoiPhuc: (version: number, ifMatch: number): TenantProfile => {
    kiemPhienBan(ifMatch);
    const dich = lichSu.find((m) => m.ban.version === version);
    if (!dich) throw loi('NOT_FOUND', 404, 'Không có phiên bản này.');
    // Trùng nội dung hiện hành: `ghi` không tạo phiên bản mới, trả lại hiện hành như backend thật.
    return ghi(chup(dich.chup), version);
  },

  /** Cho test và cho nút đặt lại khi soát bằng mắt. */
  datLai: (): void => {
    lichSu = dungLichSu();
  },
};
